import { useState, useEffect, useRef, useContext } from 'react';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import './Form.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ImageUpload from '../ImageUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Spinner from '../Spinner';
import { capitalizeFirstLetter, generateId } from '../../utils/functions';
import { SuccessToast, ErrorToast } from '../Toast/toast';
import { useCategory } from '../../CategoryContext';
import { FormControl, FormControlLabel, Paper, Radio, RadioGroup } from '@mui/material';
import CitationAndReferenceForm from './CitationAndReferenceForm';
import AuthorSearchField from './AuthorSearchField';
import { Context } from '../../AuthContext';

const PaperForm = ({ category, fields, functionality }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(Context);
  const { updateCount } = useCategory();
  const { vertical_id, id } = useParams();

  const [formData, setFormData] = useState({ vertical_id: vertical_id });
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(functionality === 'edit');
  const [authors, setAuthors] = useState([]);
  const [citations, setCitations] = useState([]);
  const [references, setReferences] = useState([]);
  const [current_citation, set_current_citation] = useState(null);
  const [current_reference, set_current_reference] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletedAuthors, setDeletedAuthors] = useState([]);

  const buttonStyle = {
    textTransform: 'none',
    margin: '10px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: '30%',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
    },
  };

  useEffect(() => {
    document.title = `${capitalizeFirstLetter(functionality)} ${capitalizeFirstLetter(
      category
    )} - The Simple Platform`;
    if (functionality === 'edit' && user) fetchItem();
  }, [functionality, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (functionality === 'add') {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDelete = (chipToDelete) => {
    console.log('Delete signal received for: ', chipToDelete);
    setAuthors((chips) => chips.filter((chip) => chip.authorId !== chipToDelete.authorId));
    setDeletedAuthors((prevDeletedAuthors) => [...prevDeletedAuthors, chipToDelete.authorId]);
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    if (functionality === 'edit') setData((prev) => ({ ...prev, image_url: file }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const formState = functionality === 'add' ? formData : data;
    let newItem = {
      ...formState,
      image_url: selectedFile,
      citations:
        current_citation && current_citation.title
          ? [...citations, { ...current_citation, paperId: generateId() }]
          : citations,
      references:
        current_reference && current_reference.title
          ? [...references, { ...current_reference, paperId: generateId() }]
          : references,
      authors,
      deleted_authors: deletedAuthors,
    };

    const [response, _id] =
      functionality === 'add' ? await addItem(newItem) : [await editItem(newItem), newItem._id];

    if (response === 200) {
      if (functionality === 'add') updateCount(`${category}s`, 1);
      SuccessToast({
        category: category,
        functionality: functionality,
        title: formState.title || formState.name,
        _id: _id,
      });

      if (functionality == 'add') {
        return navigate(
          location.pathname.slice(0, location.pathname.lastIndexOf('/')) + '?source=category_form',
          {
            state: { animation: true, new_card_id: _id },
          }
        );
      } else {
        return navigate(
          location.pathname.slice(0, location.pathname.lastIndexOf('/edit')) +
            '?source=category_form',
          {
            replace: true,
          }
        );
      }
    } else {
      ErrorToast({ category: category, functionality: functionality });
    }
    setSubmitting(false);
  };

  const addItem = async (item) => {
    const token = await user.getIdToken();
    const url = `${process.env.REACT_APP_FLASK_WEBSERVER}add_paper_manually`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(item),
    });
    const data = await res.json();
    return [res.status, data._id];
  };

  const editItem = async (item) => {
    const token = await user.getIdToken();
    const url = `${process.env.REACT_APP_FLASK_WEBSERVER}edit_${category}?id=${item._id}&vertical_id=${vertical_id}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(item),
    });
    await res.json();
    return res.status;
  };

  const fetchItem = async () => {
    const token = await user.getIdToken();
    const url = `${process.env.REACT_APP_FLASK_WEBSERVER}get_${category}_details?id=${id}&vertical_id=${vertical_id}`;
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.status === 403) {
      navigate('/forbidden', { replace: true });
      return;
    }
    const json = await res.json();
    const item = json.data;

    if (category === 'paper') {
      if (Array.isArray(item?.authors)) {
        const authors = await Promise.all(item.authors.map((author) => authorMap(author)));
        setAuthors(authors);
      }

      setData({
        ...item,
        authors: '',
        paperId: id,
        tldr: item?.tldr?.text,
        doi: item?.externalIds?.DOI,
        url: item?.openAccessPdf?.url || '',
      });

      if (Array.isArray(item?.citations)) {
        const manage_citations = await Promise.all(
          item?.citations.map(async (citation) => citationAndReferenceMap(citation))
        );
        setCitations(manage_citations);
      }

      if (Array.isArray(item.references)) {
        const manage_references = await Promise.all(
          item?.references.map(async (reference) => citationAndReferenceMap(reference))
        );
        setReferences(manage_references);
      }
    } else {
      setData(item);
    }

    setLoading(false);
  };

  const authorMap = (item) => ({
    authorId: item['authorId'],
    name: item['name'],
    db: true,
  });

  const citationAndReferenceAuthorMap = (item) => ({
    ...item,
    external_link:
      item?.external_link || item?.external_link === ''
        ? item.external_link
        : `https://www.semanticscholar.org/author/${item.authorId}`,
  });

  const citationAndReferenceMap = (item) => ({
    ...item,
    paperId: item?.paperId,
    doi: item.externalIds?.DOI || '',
    authors: Array.isArray(item.authors)
      ? item?.authors.map((author) => citationAndReferenceAuthorMap(author))
      : [],
    url: item?.openAccessPdf?.url ? item?.openAccessPdf?.url : item?.url || '',
  });

  return loading ? (
    <Spinner />
  ) : (
    <form onSubmit={submitForm} className="add-form">
      <h2 className="heading-style">
        {capitalizeFirstLetter(functionality)} {capitalizeFirstLetter(category)}
      </h2>
      {fields.map((field) => (
        <div key={field.name} style={{ textAlign: 'left' }}>
          <label className="label-style">
            {field.label}
            {field.required && <span style={{ color: 'red' }}> *</span>}
          </label>
          {field.type === 'select' ? (
            <select
              id={field.name}
              name={field.name}
              className="input-style"
              value={(functionality === 'add' ? formData : data)[field.name] || ''}
              onChange={handleChange}
              required={field.required}
              disabled={field.disabled || false}
            >
              <option value="" disabled>
                Select an option
              </option>
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : field.type === 'chip' ? (
            <AuthorSearchField
              authors={authors}
              setAuthors={setAuthors}
              vertical_id={vertical_id}
              handleDelete={handleDelete}
              field={field}
              formData={functionality === 'add' ? formData : data}
              setFormData={functionality === 'add' ? setFormData : setData}
            />
          ) : field.type === 'boolean' ? (
            <FormControl component="fieldset">
              <RadioGroup
                row
                id={field.name}
                name={field.name}
                value={(functionality === 'add' ? formData : data)[field.name] || ''}
                onChange={handleChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          ) : field.type === 'image' ? (
            <ImageUpload
              existingImageUrl={data[field.name] || ''}
              onFileSelect={handleFileSelect}
            />
          ) : !field.text_area ? (
            <input
              type={field.type}
              id={field.name}
              name={field.name}
              step={field.step ? field?.step : 1}
              className="input-style"
              placeholder={field.placeholder || ''}
              pattern={field.pattern ? field.pattern : undefined}
              title={field.title ? field.title : undefined}
              required={field.required}
              value={(functionality === 'add' ? formData : data)[field.name] || ''}
              onChange={handleChange}
              disabled={field.disabled || false}
            />
          ) : (
            <textarea
              id={field.name}
              name={field.name}
              className="input-style"
              rows={field.rows || '4'}
              required={field.required}
              value={(functionality === 'add' ? formData : data)[field.name] || ''}
              onChange={handleChange}
            />
          )}
        </div>
      ))}

      <CitationAndReferenceForm
        category="citation"
        citations={citations}
        setCitations={setCitations}
        current_citation={current_citation}
        set_current_citation={set_current_citation}
        vertical_id={vertical_id}
      />
      <CitationAndReferenceForm
        category="reference"
        citations={references}
        setCitations={setReferences}
        current_citation={current_reference}
        set_current_citation={set_current_reference}
        vertical_id={vertical_id}
      />

      {!submitting ? (
        <div>
          <Button type="submit" variant="outlined" disableRipple={true} sx={buttonStyle}>
            {functionality === 'add' ? `Add ${capitalizeFirstLetter(category)}` : 'Save Edits'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            disableRipple={true}
            onClick={() => {
              navigate(
                location.pathname.slice(0, location.pathname.lastIndexOf(`/${functionality}`)) +
                  '?source=category_form',
                {
                  replace: true,
                }
              );
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ marginTop: '20px' }} />
      )}
    </form>
  );
};

export default PaperForm;
